import { CrossIcon, KeyIcon, PlusIcon, Site, UserIcon } from '@umanforge/common';
import { useSftpSection } from '../hooks/sftp_site_section.hook';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/inputs/input.component';
import { ModifySite } from 'interfaces/api_site.interfaces';
import ReactSwitch from 'react-switch';
import { Tooltip } from 'react-tooltip';
import { BinIcon } from 'components/icons/bin_icon.component';
import { BaseModal } from 'components/modals/base_modal.component';
import { SaveIcon } from 'components/icons/save_icon.component';
import { Button } from 'components/inputs/button.component';
import { EmailIcon } from 'components/icons/email_icon.component';


interface SftpSiteSectionProps {
  site: Site;
  modifySite: ModifySite;
  setModifySite: ( item: ModifySite ) => void;
  sftpStatus: boolean;
  setSftpStatus: ( item: boolean ) => void;
  setDefaultSftpStatus: ( item: boolean ) => void;
  newEmails: string[]
  setNewEmails: ( item: string[] ) => void;
  removedEmails: string[]
  setRemovedEmails: ( item: string[] ) => void;
}

export function SftpSiteSection( props: SftpSiteSectionProps ) {
  const { site, modifySite, setModifySite, sftpStatus, setSftpStatus, setDefaultSftpStatus, newEmails, setNewEmails, removedEmails, setRemovedEmails } = props;

  const { t } = useTranslation();
  const {
    isActive,
    onSftpStatusChanged,
    currentEmails,
    onAddEmailClick, onCancelAddEmailClick,
    onRemoveEmailClick, onCancelRemoveEmailClick,
    isModalOpen, setIsModalOpen,
    newEmail, setNewEmail,
    checkEmail
  } = useSftpSection( site, setSftpStatus, setDefaultSftpStatus, setModifySite, modifySite, newEmails, setNewEmails, removedEmails, setRemovedEmails );

  return (
    <>
      <details
        className="group w-full rounded-lg border border-gray-200 bg-white shadow-sm"
        onToggle={ e => { onSftpStatusChanged( e.currentTarget.open ); } }
        open={ sftpStatus }
      >
        <summary className="rounded-lg flex cursor-pointer list-none items-center justify-between px-4 py-3 font-medium text-gray-800 hover:bg-gray-50 [&::-webkit-details-marker]:hidden select-none">
          { t( 'translation:sftp_setting' ) }
          <ReactSwitch
            checked={ sftpStatus }
            onChange={ onSftpStatusChanged }
          />
        </summary>
        <div className="border-t flex flex-col gap-6 border-gray-200 px-4 py-3 text-sm text-gray-600">

          <div className='flex flex-col gap-4 w-full'>
            <Input
              label={ t( 'translation:public_key' ) }
              placeholder={ t( 'translation:public_key' ) + '...' }
              icon={ <KeyIcon className='rotate-[20deg]'/> }
              required
              value={ modifySite.sftpPublicKey ?? site.sftpPublicKey ?? '' }
              onChange={ newValue => {
                modifySite.sftpPublicKey = ( site.sftpPublicKey ?? '' ) != newValue ? newValue : undefined;
                setModifySite( structuredClone( modifySite ) );
              } }
            />
            {
              isActive && site.sftpUsername &&
                <Input
                  label={ t( 'translation:username' ) }
                  placeholder={ t( 'translation:username' ) + '...' }
                  icon={ <UserIcon/> }
                  disabled={ true }
                  value={ site.sftpUsername ?? '' }
                />
            }
          </div>

          <div className='flex w-full'>
            {
              isActive && site.sftpUsername ?
                <div className='flex flex-col gap-2 w-full justify-center'>
                  <div className='flex gap-4'>
                    <span className='text-base content-center' id='emails_info'> { t( 'translation:emails' ) } </span>
                    <div>
                      <Button
                        padding={ 2 }
                        icon={ <PlusIcon className='h-6 w-6 text-white'/> }
                        onClick={ () => { setIsModalOpen( true ); } }
                      />
                    </div>
                  </div>
                  <Tooltip
                    anchorSelect='#emails_info'
                    className='z-[1000] max-w-[400px] text-wrap'
                    place='right'
                  >
                    { t( 'translation:tooltip_sftp_emails_infos' ) }
                  </Tooltip>
                  <div className='relative flex flex-col w-full h-fit max-h-[400px] overflow-y-auto p-4 rounded-lg bg-gray-100 shadow-inner'>

                    {
                      currentEmails.length == 0 && newEmails.length == 0 && (
                        <span className='py-8 w-[60%] self-center text-center italic'>
                          { t( 'no_emails_register' ) }
                        </span>
                      )
                    }


                    <div className='flex flex-col w-full divide-y-2'>
                      {
                        currentEmails.map( ( email, index ) => {
                          const removedIndexList = removedEmails.findIndex( removedEmail => { return email === removedEmail; } );
                          const isInRemovedList = removedIndexList !== -1;
                          return (
                            <div className='py-0.5' key={ 'current_email_' + index }>
                              <div className={ `flex w-full py-1.5 px-1.5 gap-2 items-center justify-center rounded-lg ${ isInRemovedList ? 'bg-red-600/30' : '' }` }>
                                <div>
                                  <Button
                                    icon={ !isInRemovedList ? <BinIcon className='h-[24px] w-[24px] stroke-white'/> : <CrossIcon className='h-[24px] w-[24px] stroke-white'/> }
                                    color={ !isInRemovedList ? '#f87171' : '#0bb173' }
                                    colorHover={ !isInRemovedList ? '#ef4444' : '#088D5C' }
                                    onClick={ () => {
                                      if ( isInRemovedList ) {
                                        onCancelRemoveEmailClick( removedIndexList );
                                      } else {
                                        onRemoveEmailClick( index );
                                      }
                                    } }
                                  />
                                </div>
                                <span className='w-full h-full content-center'> { email } </span>
                              </div>
                            </div>
                          );
                        } )
                      }
                    </div>

                    {
                      currentEmails.length > 0 && newEmails.length > 0 && (
                        <div className='w-full h-[1px] bg-gray-200 rounded-full'/>
                      )
                    }

                    <div className='flex flex-col w-full divide-y-2'>
                      {
                        newEmails.map( ( email, index ) => {
                          return (
                            <div className='py-0.5' key={ 'new_email_' + index }>
                              <div className='flex w-full py-1.5 px-1.5 gap-2 items-center justify-center rounded-lg bg-green-600/30' key={ 'new_email_' + index }>
                                <div>
                                  <Button
                                    icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                                    color='#f87171'
                                    colorHover='#ef4444'
                                    onClick={ () => { onCancelAddEmailClick( index ); } }
                                  />
                                </div>
                                <span className='w-full h-full content-center'> { email } </span>
                              </div>
                            </div>
                          );
                        } )
                      }
                    </div>
                  </div>
                </div>
                :
                <span className='w-full text-center items-center justify-center px-6 py-4'>
                  { t( 'translation:details_for_active_sftp' ) }
                </span>
            }
          </div>

        </div>

      </details>

      <BaseModal
        id={ 'add_new_email' }
        title={ t( 'translation:add_new_email' ) }
        isOpen={ isModalOpen }
        onClose={ () => { setIsModalOpen( false ); } }
        hasCross={ true }
      >
        <div className='flex flex-col gap-4'>
          <span className='px-2 pt-2 text-gray-800 italic'>
            { t( 'translation:enter_a_new_email_address' ) }
          </span>
          <div className='px-2'>
            <Input
              placeholder={ t( 'translation:Email' ) + '...' }
              icon={ <EmailIcon className='h-8 w-8'/> }
              required
              value={ newEmail }
              onChange={ setNewEmail }
              checkContent={ checkEmail }
            />
          </div>
          <div className='justify-self-end'>
            <Button
              label={ t( 'save' ) }
              disable={ checkEmail( newEmail ) != null }
              icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
              color='#0bb173'
              colorHover='#088D5C'
              onClick={ () => {
                onAddEmailClick( newEmail );
                setNewEmail( '' );
                setIsModalOpen( false );
              } }
            />
          </div>
        </div>
      </BaseModal>

    </>
  );
}